/**
 * Deterministic planet terrain pipeline — shared by client renderer and server
 * generator. Everything derives from { seed, type } + the hexasphere tiles, so
 * the client can rebuild the full-detail planet from three stored values while
 * the server persists only the gameplay arrays (terrains/heights/forests/
 * resources). Ported from labs/planet-3rdgen (branch volcano).
 *
 * Self-contained: noise is our own fBm on a vendored SimplexNoise — no three,
 * no CDN — so node on the server can import this directly.
 *
 * NOTE: the game hexasphere uses Y as the polar axis in the Three.js scene
 * (see getLatLon in hexasphere.js), so latitude comes from center.y here.
 */

import { SimplexNoise } from './simplex-noise.js';

// Noise is sampled on a fixed reference sphere, independent of the hexasphere
// radius: planet size only changes tessellation density, patterns are pure seed.
export const NOISE_RADIUS = 64 * 1000;
const RISE = NOISE_RADIUS * 0.013; // height gained per hex-step inland (renderer scale)
const MTN_H = 10; // mountain plug height on the renderer's 0-20 slider (calibrated standard)

// deterministic PRNG: hash the seed (xfnv-1a) then mulberry32 → { random() }
export function seededRng(seed) {
	const s = String(seed);
	let h = 2166136261 >>> 0;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	let a = h >>> 0;
	return {
		random() {
			a = (a + 0x6d2b79f5) | 0;
			let t = Math.imul(a ^ (a >>> 15), 1 | a);
			t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
			return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
		},
	};
}

export const NOISE_TYPES = { FBM: 'FBM', BILLOWING: 'BILLOWING', RIGID: 'RIGID' };

export const DEFAULT_NOISE_PARAMS = {
	octaves: 13,
	persistence: 0.707,
	lacunarity: 1.8,
	exponentiation: 4.5,
	height: 300.0,
	scale: 1100.0,
	seed: 0,
	noiseType: NOISE_TYPES.FBM,
};

export class Noise {
	constructor(params) {
		this.params = { ...DEFAULT_NOISE_PARAMS, ...params };
		const simplex = new SimplexNoise(seededRng(this.params.seed ?? 0));
		const n = (x, y, z) => simplex.noise3d(x, y, z);
		this.noiseFunctions = {
			[NOISE_TYPES.FBM]: n,
			[NOISE_TYPES.BILLOWING]: (x, y, z) => Math.abs(n(x, y, z)),
			[NOISE_TYPES.RIGID]: (x, y, z) => 1.0 - Math.abs(n(x, y, z)),
		};
	}

	get(x, y, z) {
		const p = this.params;
		const G = 2.0 ** -p.persistence;
		const xs = x / p.scale, ys = y / p.scale, zs = z / p.scale;
		const nf = this.noiseFunctions[p.noiseType];
		let amplitude = 1.0, frequency = 1.0, normalization = 0, total = 0;
		for (let o = 0; o < p.octaves; o++) {
			const v = nf(xs * frequency, ys * frequency, zs * frequency) * 0.5 + 0.5;
			total += v * amplitude;
			normalization += amplitude;
			amplitude *= G;
			frequency *= p.lacunarity;
		}
		total /= normalization;
		return Math.pow(total, p.exponentiation) * p.height;
	}
}

const clamp01 = x => (x < 0 ? 0 : x > 1 ? 1 : x);

// elevation as a pure function of position on the NOISE_RADIUS sphere;
// seaLevelFactor is per PLANET TYPE (ocean worlds drown, earthlike has oceans)
export function createHeight(seed, seaLevelFactor = 1 / 200) {
	const radius = NOISE_RADIUS;
	const continents = new Noise({ seed, height: radius / 14, scale: radius / 1.4 });
	const mountains = new Noise({ seed: seed + '-mtn', height: radius / 8, scale: radius / 6 });
	const detail = new Noise({ seed: seed + '-detail', height: radius / 130, scale: radius / 30 });
	const SEA_LEVEL = radius * seaLevelFactor;

	return (x, y, z) => {
		const base = continents.get(x, y, z) - SEA_LEVEL;
		if (base <= 0) return base + detail.get(x, y, z) * 0.3;
		const landness = clamp01(base / (radius / 16));
		const ridge = Math.abs(mountains.get(x, y, z)) * landness * landness;
		return base + ridge + detail.get(x, y, z);
	};
}

// ---- PLANET TYPES — everything a type changes lives in this table ----------
// biome rows are [r,g,b, atlas-cell]; colour strings feed the client shader
// (harmless server-side). zone ids drive the mountain-texture tint: 0 neutral,
// 1 ice/white, 2 desert/cream, 3 tundra/pale. forest maps atlas-cell → chance.
// GAME CANON: only EARTHLIKE and OCEAN have water. Dry types are all land with
// mountains from a dedicated noise. GAS is a flat hexgrid sphere (facilities
// only) with painted bands — palette derived from the seed (see gasPalette).
const B_ICE = [0.918, 0.949, 0.984, 2], B_TUN = [0.553, 0.561, 0.463, 8], B_PLA = [0.541, 0.604, 0.333, 4],
	B_GRA = [0.290, 0.486, 0.227, 5], B_DES = [0.804, 0.718, 0.518, 6], B_LAV = [0.10, 0.02, 0.0, 10];
const WATER = { deep: '0.0392,0.1412,0.2510', shallow: '0.1137,0.4157,0.6588', beach: '0.8471,0.7804,0.6039' };
export const PLANET_TYPES = {
	EARTHLIKE: {
		biome: lat => (lat > 72 ? B_ICE : lat > 63 ? B_TUN : lat > 36 ? B_PLA : lat > 9 ? B_GRA : B_DES),
		zone: cell => (cell === 2 ? 1 : cell === 6 ? 2 : cell === 8 ? 3 : 0),
		mtnCell: 9, mtnShare: 0.22, volcanoShare: 0.3, seaFactor: 1 / 200, snow: 0.8,
		forest: { 4: 0.35, 5: 0.5, 8: 0.15 },
		...WATER, atmo: '0.3, 0.6, 1.0',
	},
	OCEAN: { // island world — high sea level, archipelagos
		biome: lat => (lat > 72 ? B_ICE : lat > 60 ? B_TUN : lat > 30 ? B_GRA : B_PLA),
		zone: cell => (cell === 2 ? 1 : cell === 8 ? 3 : 0),
		mtnCell: 9, mtnShare: 0.3, volcanoShare: 0.4, seaFactor: 1 / 55, snow: 0.8,
		forest: { 4: 0.3, 5: 0.5, 8: 0.15 },
		...WATER, atmo: '0.25, 0.55, 1.0',
	},
	DESERT: { dry: true,
		biome: lat => (lat > 60 ? B_TUN : B_DES),
		zone: () => 2,
		mtnCell: 9, mtnShare: 0.3, volcanoShare: 0.35, snow: 1.0,
		forest: {},
		...WATER, atmo: '0.85, 0.62, 0.35',
	},
	BARREN: { dry: true, // grey rock world
		biome: () => B_TUN,
		zone: () => 3,
		mtnCell: 9, mtnShare: 0.38, volcanoShare: 0.3, snow: 1.0,
		forest: {},
		...WATER, atmo: '0.5, 0.52, 0.58',
	},
	ICE: { dry: true,
		biome: () => B_ICE,
		zone: () => 1,
		mtnCell: 9, mtnShare: 0.28, volcanoShare: 0.1, snow: 1.0,
		forest: {},
		...WATER, atmo: '0.6, 0.8, 1.0',
	},
	TUNDRA: { dry: true,
		biome: lat => (lat > 52 ? B_ICE : B_TUN),
		zone: cell => (cell === 2 ? 1 : 3),
		mtnCell: 9, mtnShare: 0.3, volcanoShare: 0.25, snow: 1.0,
		forest: { 8: 0.15 },
		...WATER, atmo: '0.55, 0.7, 0.85',
	},
	LAVA: { dry: true, // lava fields + black volcanic mountains, cracks glow
		biome: () => B_LAV,
		zone: () => 0,
		mtnCell: 12, mtnShare: 0.35, volcanoShare: 1, snow: 1.0,
		forest: {},
		...WATER, atmo: '1.0, 0.32, 0.12',
	},
	MARSH_JUNGLE: { // earthlike without ice poles or desert zone; forest-heavy
		biome: lat => (lat > 63 ? B_TUN : lat > 36 ? B_PLA : B_GRA),
		zone: cell => (cell === 8 ? 3 : 0),
		mtnCell: 9, mtnShare: 0.16, volcanoShare: 0.25, seaFactor: 1 / 130, snow: 0.9,
		forest: { 4: 0.7, 5: 0.85, 8: 0.35 },
		...WATER, atmo: '0.35, 0.65, 0.8',
	},
	GAS: { gas: true, // flat hexgrid sphere; painted bands (see gasPalette)
		biome: () => B_TUN, zone: () => 0,
		mtnCell: 9, mtnShare: 0, volcanoShare: 0, snow: 1.0,
		forest: {},
		...WATER, atmo: '0.85, 0.72, 0.5',
		g1: '0.83,0.70,0.50', g2: '0.62,0.46,0.33', g3: '0.93,0.88,0.74',
	},
};

// per-seed gas palette — every gas giant looks a little different (client shader)
export function gasPalette(seed) {
	const rng = seededRng(seed + '-gas');
	const h2r = (h, s, l) => { // tiny hsl→"r,g,b"
		const a = s * Math.min(l, 1 - l);
		const f = n => {
			const k = (n + h * 12) % 12;
			return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
		};
		return `${f(0).toFixed(3)},${f(8).toFixed(3)},${f(4).toFixed(3)}`;
	};
	const hue = rng.random();
	return {
		g1: h2r(hue, 0.30 + rng.random() * 0.2, 0.60),
		g2: h2r((hue + 0.03 + rng.random() * 0.05) % 1, 0.42, 0.40),
		g3: h2r((hue + 0.9) % 1, 0.25, 0.82),
		bandFreq: 10 + rng.random() * 12,
		atmo: h2r(hue, 0.5, 0.55),
	};
}

/**
 * The tile-level pipeline: land/water, biome cells, mountains, volcanoes, lava
 * rings, forests, base heights — all deterministic from (seed, type).
 *
 * @param sphere { centers: [[x,y,z]…], neighbors: [[i…]…] } from planetHexasphere
 * @param seed   string
 * @param type   PLANET_TYPES key
 */
export function generateTerrain(sphere, { seed, type }) {
	const PT = PLANET_TYPES[type] || PLANET_TYPES.EARTHLIKE;
	const { centers, neighbors: nbr } = sphere;
	const N = centers.length;

	// PASS 1 — normalized directions, raw noise height, land/water per hex
	const cx = new Float32Array(N), cy = new Float32Array(N), cz = new Float32Array(N);
	const land = new Uint8Array(N);
	const sampH = new Float32Array(N);
	const sample = createHeight(seed, PT.seaFactor || 1 / 200);
	for (let i = 0; i < N; i++) {
		const c = centers[i];
		const m = Math.sqrt(c[0] * c[0] + c[1] * c[1] + c[2] * c[2]) || 1;
		cx[i] = c[0] / m; cy[i] = c[1] / m; cz[i] = c[2] / m;
		const sh = sample(cx[i] * NOISE_RADIUS, cy[i] * NOISE_RADIUS, cz[i] * NOISE_RADIUS);
		sampH[i] = sh;
		land[i] = PT.dry || PT.gas ? 1 : sh > 0 ? 1 : 0; // dry/gas worlds have no water
	}

	// PASS 2 — BFS distance from coast (both directions)
	const bfs = isSource => {
		const dist = new Int16Array(N).fill(9999);
		const q = [];
		for (let i = 0; i < N; i++) if (isSource(i)) { dist[i] = 0; q.push(i); }
		for (let h = 0; h < q.length; h++) {
			const t = q[h];
			for (const nb of nbr[t]) if (dist[nb] > dist[t] + 1) { dist[nb] = dist[t] + 1; q.push(nb); }
		}
		return dist;
	};
	const distToWater = bfs(i => !land[i]);
	const distToLand = bfs(i => land[i]);

	// MOUNTAINS — a fixed share of tiles, chosen deterministically: wet worlds
	// rank inland tiles by the terrain noise; dry worlds have no coast structure,
	// so they use a dedicated noise and a bigger share. Gas planets have none.
	const isMtnTile = new Uint8Array(N);
	if (!PT.gas) {
		const cand = [];
		if (PT.dry) {
			const mn = new Noise({ seed: seed + '-mtn2', octaves: 3, exponentiation: 1, height: 1, scale: NOISE_RADIUS / 5 });
			const score = new Float32Array(N);
			for (let i = 0; i < N; i++) {
				score[i] = mn.get(cx[i] * NOISE_RADIUS, cy[i] * NOISE_RADIUS, cz[i] * NOISE_RADIUS);
				cand.push(i);
			}
			cand.sort((a, b) => score[b] - score[a] || a - b);
		} else {
			for (let i = 0; i < N; i++) if (land[i] && distToWater[i] >= 1) cand.push(i);
			cand.sort((a, b) => sampH[b] - sampH[a] || a - b);
		}
		for (let k = 0; k < Math.floor(cand.length * PT.mtnShare); k++) isMtnTile[cand[k]] = 1;
	}

	// dry worlds: gentle rolling base heights from a broad noise (no coast distance)
	const dryBase = new Float32Array(N);
	if (PT.dry) {
		const bn = new Noise({ seed: seed + '-base', octaves: 4, exponentiation: 1, height: 1, scale: NOISE_RADIUS / 3 });
		for (let i = 0; i < N; i++) dryBase[i] = bn.get(cx[i] * NOISE_RADIUS, cy[i] * NOISE_RADIUS, cz[i] * NOISE_RADIUS) * 4 - 0.5;
	}

	// height bonus per direct mountain neighbour — cluster cores rise into peaks
	const mtnNbrs = new Uint8Array(N);
	for (let i = 0; i < N; i++) if (isMtnTile[i]) for (const nb of nbr[i]) if (isMtnTile[nb]) mtnNbrs[i]++;

	// VOLCANOES — a per-type share of LONE mountains becomes a volcano
	const isVolcano = new Uint8Array(N);
	{
		const rng = seededRng(seed + '-volcano');
		for (let i = 0; i < N; i++) if (isMtnTile[i] && mtnNbrs[i] === 0 && rng.random() < PT.volcanoShare) isVolcano[i] = 1;
	}

	// on NON-volcanic worlds a volcano scorches its surroundings: the land ring
	// around it turns to lava tiles (atlas cell 10)
	const isLavaRing = new Uint8Array(N);
	if (type !== 'LAVA') {
		for (let i = 0; i < N; i++) if (isVolcano[i]) for (const nb of nbr[i]) if (land[nb] && !isMtnTile[nb]) isLavaRing[nb] = 1;
	}

	// PER-TILE biome by the tile's own latitude — each hex is ONE biome.
	// The game hexasphere is Y-up in the scene, so latitude comes from cy.
	const latCell = new Uint8Array(N);
	const zoneId = new Uint8Array(N);
	const biomeRgb = new Float32Array(N * 3);
	for (let i = 0; i < N; i++) {
		const lat = Math.abs(Math.asin(Math.max(-1, Math.min(1, cy[i]))) * 57.2958);
		const c = PT.biome(lat);
		biomeRgb[i * 3] = c[0]; biomeRgb[i * 3 + 1] = c[1]; biomeRgb[i * 3 + 2] = c[2];
		latCell[i] = c[3];
		zoneId[i] = PT.zone(c[3]);
	}

	// final atlas cell: mountains wear the type's mountain cell (9, or 12 on
	// LAVA planets), volcanoes always the lava-rock cell 12, lava rings cell 10
	const cell = new Uint8Array(N);
	for (let i = 0; i < N; i++) {
		cell[i] = PT.gas ? 11
			: !land[i] ? 0
			: isMtnTile[i] ? (isVolcano[i] ? 12 : PT.mtnCell)
			: isLavaRing[i] ? 10
			: latCell[i];
	}

	// FORESTS — per-biome chance (heavy on MARSH_JUNGLE), never on water,
	// mountains or scorched ground; strength 1–3
	const forest = new Uint8Array(N);
	{
		const rng = seededRng(seed + '-forest');
		for (let i = 0; i < N; i++) {
			const chance = PT.forest[cell[i]];
			if (!chance) continue;
			const roll = rng.random(); // one roll per candidate keeps the stream stable
			if (roll < chance) forest[i] = 1 + Math.floor(rng.random() * 3);
		}
	}

	// base heights in hex-steps (renderer scale: ×RISE), matching the lab:
	// wet = coast distance, dry = rolling noise; mountain tiles gain ¼ of the
	// plug height per direct mountain neighbour; gas planets are flat spheres
	const baseH = new Float32Array(N);
	for (let i = 0; i < N; i++) {
		if (PT.gas) continue;
		if (land[i]) {
			const base = PT.dry ? dryBase[i] : distToWater[i] - 0.5;
			const nbrBonus = isMtnTile[i] ? mtnNbrs[i] * 0.25 * ((NOISE_RADIUS * 0.0045 * MTN_H) / RISE) : 0;
			baseH[i] = base + nbrBonus;
		} else {
			baseH[i] = -(distToLand[i] - 0.5);
		}
	}

	return { N, PT, land, cell, latCell, zoneId, biomeRgb, isMtnTile, isVolcano, isLavaRing, mtnNbrs, distToWater, distToLand, baseH, forest, sampH };
}

// ---- DERIVED TERRAIN — feed a planet (seed) and a tile index, get what is
// supposed to be there. TERRAIN IS NOT DATA: only { seed, genVersion, size,
// type } is stored, plus a sparse `patches` object for gameplay overrides
// (terraforming, tech, events can turn a desert tile to grassland or change a
// height — those are the ONLY per-tile terrain facts a planet document keeps).
// Resources/forests are rolled at creation against this derivation and stored
// (they are NOT derivable — see planet-generator.js).
//
// PERSISTENCE CONTRACT: stored resources/settlements are keyed to the terrain
// as derived at creation. The pipeline is therefore version-frozen: changes
// bump GEN_VERSION, and every planet stores a terrainHash so any drift (engine
// float differences, accidental pipeline edits) fails LOUDLY instead of
// silently putting resources on the wrong tiles.

import { getSphereMath } from './hexasphere.js';

export const GEN_VERSION = 2;

const deriveCache = new Map(); // key → { terrains, heights, hash, verified }
const DERIVE_CACHE_MAX = 64;

// derived base terrain for a planet, cached once per process (6-12ms first
// touch, O(1) after). NO patches applied here — this is the immutable base.
export function derivePlanet(planet) {
	const seed = planet.seed ?? planet.name ?? 'Miserit';
	const key = `${planet.type}|${planet.size}|${seed}|${planet.genVersion ?? GEN_VERSION}`;
	let data = deriveCache.get(key);
	if (!data) {
		const sphere = getSphereMath(planet.type, planet.size);
		const N = sphere.centers.length;
		data = { terrains: new Uint8Array(N), heights: new Float32Array(N) };

		if (PLANET_TYPES[planet.type]) { // ASTEROIDS etc. stay bare geometry
			const terr = generateTerrain(sphere, { seed, type: planet.type });
			for (let i = 0; i < N; i++) {
				data.terrains[i] = terr.cell[i];
				// gameplay height: ≤0 water, land low, mountains high (>0.75)
				data.heights[i] = terr.PT.gas ? 0
					: !terr.land[i] ? -Math.min(1, terr.distToLand[i] * 0.25)
					: terr.isMtnTile[i] ? 0.8 + Math.min(0.19, terr.mtnNbrs[i] * 0.04)
					: Math.min(0.7, Math.max(0.05, 0.05 + terr.baseH[i] * 0.1));
			}
		}
		data.hash = terrainHash(data);

		if (deriveCache.size >= DERIVE_CACHE_MAX) deriveCache.delete(deriveCache.keys().next().value);
		deriveCache.set(key, data);
	}

	// drift guard — once per cache entry
	if (!data.verified && planet.terrainHash) {
		data.verified = true;
		if (planet.terrainHash !== data.hash) {
			const msg = `TERRAIN DRIFT on planet "${planet.name}" (seed ${seed}): stored hash ${planet.terrainHash} != derived ${data.hash} — stored resources/settlements no longer match the derivation!`;
			(globalThis.l || console.error)(msg);
		}
	}
	return data;
}

// FNV-1a over terrain cells + heights quantized to 1/1000 (robust against
// last-ulp float differences, sensitive to any real terrain change)
export function terrainHash({ terrains, heights }) {
	let h = 2166136261 >>> 0;
	for (let i = 0; i < terrains.length; i++) {
		h = Math.imul(h ^ terrains[i], 16777619);
		h = Math.imul(h ^ (Math.round(heights[i] * 1000) & 0xffff), 16777619);
	}
	return h >>> 0;
}

// THE lookup: seed + tile index → what is there, patch overrides applied.
// planet.patches = { [tileIndex]: { t?, h? } } — sparse, gameplay-written.
export function planetTile(planet, index) {
	const base = derivePlanet(planet);
	const patch = planet.patches?.[index];
	return {
		t: patch?.t ?? base.terrains[index],
		h: patch?.h ?? base.heights[index],
	};
}

// full patched view for mesh builders: base arrays, copied only when patches
// exist. Mesh rebuilds are rare — deriving stays the cached hot path.
export function planetTerrains(planet) {
	const base = derivePlanet(planet);
	const patches = planet.patches;
	if (!patches || Object.keys(patches).length === 0) {
		return { terrains: base.terrains, heights: base.heights };
	}
	const terrains = base.terrains.slice();
	const heights = base.heights.slice();
	for (const i in patches) {
		if (patches[i].t !== undefined) terrains[i] = patches[i].t;
		if (patches[i].h !== undefined) heights[i] = patches[i].h;
	}
	return { terrains, heights };
}
